/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Box,
  Button,
  FormHelperText,
  Grid,
  MenuItem,
  Tooltip,
} from '@mui/material';
import React from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { ReactSortable } from 'react-sortablejs';
import {
  getDeepProperty,
  isEmpty,
  ReactHookFormHiddenInput,
  ReactHookFormSelect,
  ReactHookFormTextField,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import {
  AlertBanner,
  CustomIconButton,
  SwitchButton,
  ToggleMenu,
  useConfirmationDialog,
  usePreventBrowserClose,
} from '@opengeoweb/shared';
import type { MapPreset } from '@opengeoweb/store';
import { Add, Edit, Expire, Remove } from '@opengeoweb/theme';
import { CollapseToggleButton } from './CollapseToggleButton';
import type { DayRange } from './DaySelector';
import { DaySelector } from './DaySelector';
import {
  buildDefaultWarningItem,
  buildDuplicateWarningItem,
  getDefaultDayRangeForType,
  warningEventModes,
  warningEventTypes,
} from './types';
import {
  buildSubmitValues,
  getEventStatusActions,
  getFormData,
  getWarningStatusesForEventAction,
  isWarningFinalised,
} from './utils';
import { buildAreaFromFeature, WarningEventItem } from './WarningEventItem';
import {
  getTimelineRange,
  WarningEventTimelineRangeProvider,
} from './WarningEventTimeline';
import type { DrawingListItem } from '../../../store/warningsDrawings/types';
import type { FormError } from '../../../store/warningsEventForm/reducer';
import type {
  WarningEvent,
  WarningEventTexts,
} from '../../../store/warningsForm/types';
import {
  PublicWarningStatus,
  WarningEventMode,
  WarningEventType,
} from '../../../store/warningsForm/types';
import { useWarningsTranslation } from '../../../utils/i18n';
import { useFormDirty } from '../../../utils/useFormDirty';
import { getErrors, useScrollTopOnError } from '../../../utils/warningUtils';
import type { WarningLevel } from '../../PublicWarningsForm/LevelField';
import { Phenomenon } from '../../PublicWarningsForm/PhenomenonField';
import WarningAvatar from '../../PublicWarningsFormDialog/WarningsAvatar';
import {
  SUMMARY_CONCISE_SUMMARY_LIMIT,
  SUMMARY_DESCRIPTION_LIMIT,
  SUMMARY_HEADLINE_LIMIT,
  SUMMARY_FIELDS_MIN_LENGTH,
} from '../WarningEventSummaryDialog/WarningEventSummaryForm';

export const INCIDENT_MAX_LENGTH = 50;

const TEST_MESSAGE_PREFIX_ORIGINAL = 'DIT IS EEN TEST BERICHT';
const TEST_MESSAGE_PREFIX_TRANSLATION = 'THIS IS A TEST MESSAGE';

export const CROSS_FIELD_VALIDATIONS: Record<
  string,
  {
    fields: string[];
    translationKey: string;
  }
> = {
  'texts.headlineOriginal': {
    fields: ['texts.conciseSummaryOriginal', 'texts.descriptionOriginal'],
    translationKey: 'warning-event-validation-summary-incomplete',
  },
  'texts.headlineTranslation': {
    fields: ['texts.conciseSummaryTranslation', 'texts.descriptionTranslation'],
    translationKey: 'warning-event-validation-translation-incomplete',
  },
};

export const getCrossFieldError = (
  anchorField: string,
  errors: FieldErrors,
): string | undefined => {
  const rules = CROSS_FIELD_VALIDATIONS[anchorField];
  return rules.fields.some((field) => getDeepProperty(field.split('.'), errors))
    ? rules.translationKey
    : undefined;
};

export interface WarningEventFormContentProps {
  defaultMapPreset?: MapPreset;
  error?: FormError;
  currentEventEditor?: string;
  isSummaryDialogOpen?: boolean;
  selectedDrawing?: DrawingListItem;
  hoveredDrawing?: DrawingListItem;
  approvedArea?: GeoJSON.FeatureCollection;
  isApprovedAreaLoading?: boolean;
  selectedEvent?: WarningEvent;
  shouldHideFormToggleMode?: boolean;
  isReadOnly?: boolean;
  isSnapshot?: boolean;
  isDefaultCollapsed?: boolean;
  onCancel?: () => void;
  onAddDrawing?: () => void;
  onChangeFormMode?: (isReadOnly: boolean) => void;
  onDelete?: () => void;
  onFormDirty?: (isFormDirty: boolean) => void;
  onOpenWarningSummary?: () => void;
  onSaveDraft?: (formValues: WarningEvent) => Promise<Headers | null>;
  onPublish?: (formValues: WarningEvent) => Promise<Headers | null>;
}

export const WarningEventFormContent: React.FC<
  WarningEventFormContentProps
> = ({
  defaultMapPreset,
  currentEventEditor,
  error = { severity: 'error', message: '' },
  shouldHideFormToggleMode = false,
  isReadOnly = false,
  isSnapshot = false,
  isDefaultCollapsed = false,
  onCancel = (): void => {},
  onAddDrawing = (): void => {},
  onChangeFormMode = (): void => {},
  onDelete = (): void => {},
  onFormDirty = (): void => {},
  onOpenWarningSummary = (): void => {},
  onSaveDraft = async (formValues: WarningEvent): Promise<null> => null,
  onPublish = async (formValues: WarningEvent): Promise<null> => null,
  isSummaryDialogOpen = false,
  selectedDrawing,
  hoveredDrawing,
  approvedArea,
  isApprovedAreaLoading = false,
  selectedEvent,
}) => {
  const { t } = useWarningsTranslation();
  const confirmDialog = useConfirmationDialog();
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [isUnpublishedExpiredWithdraw, setIsUnpublishedExpiredWithdraw] =
    React.useState(false);

  const [pendingEventAction, setPendingEventAction] = React.useState<
    PublicWarningStatus.EXPIRED | PublicWarningStatus.WITHDRAWN | null
  >(null);

  const { toggleIsDraft, DraftFieldHelper } = useDraftFormHelpers();

  const {
    control,
    getValues,
    handleSubmit,
    formState,
    reset,
    resetField,
    setValue,
    trigger,
  } = useFormContext<WarningEvent>();

  const { dirtyFields, errors: fieldErrors } = formState;
  const isDirty = Object.keys(dirtyFields).length > 0;

  const showSummaryError = getCrossFieldError(
    'texts.headlineOriginal',
    fieldErrors,
  );
  const showTranslationError = getCrossFieldError(
    'texts.headlineTranslation',
    fieldErrors,
  );

  usePreventBrowserClose(isDirty);
  const ref = useScrollTopOnError(error);
  const errors = getErrors(error.message);

  const { fields, append, insert, remove, move } = useFieldArray({
    control,
    name: 'warnings',
  });

  const warnings = useWatch({ control, name: 'warnings' }) ?? [];
  const eventStatus = useWatch({ control, name: 'status' });
  const linkedToId = useWatch({ control, name: 'linked_to_id' });
  const { canExpire, canWithdraw, hasPendingWarnings } =
    getEventStatusActions(warnings);
  const isEventFinalised =
    warnings.length > 0 && warnings.every(isWarningFinalised);
  const areEventFieldsDisabled = isReadOnly || isEventFinalised;
  const showEventOptionsMenu =
    !isReadOnly &&
    linkedToId !== undefined &&
    eventStatus === PublicWarningStatus.DRAFT_PUBLISHED;

  // When switching event, reset the form to the new values
  const eventId = selectedEvent?.eventId;
  React.useEffect(() => {
    // sync redux with react-hook-form
    reset(getFormData(selectedEvent));
  }, [eventId, reset, selectedEvent]);

  // When switching event, clear any pending event level expire/withdraw action
  React.useEffect(() => {
    setPendingEventAction(null);
  }, [eventId]);

  // When the editor gets set back to viewer and the form is dirty, reset any changes made
  React.useEffect(() => {
    if (isReadOnly && Object.keys(dirtyFields).length > 0) {
      reset(getFormData(selectedEvent));
      setPendingEventAction(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly]);

  // Apply a drawing picked in the Area & Drawing Loader to the form
  React.useEffect(() => {
    if (getValues('selectedDrawing')?.id === selectedDrawing?.id) {
      return;
    }
    setValue('selectedDrawing', selectedDrawing, { shouldDirty: true });
    // the areas of all warnings belong to the previous drawing, clear them.
    // When the new drawing has a single object, assign it to every warning.
    // Finalised (expired/withdrawn) warnings are read-only, keep their areas.
    // While the approved area is loading the object can not be clipped yet,
    // the warning item assigns it once the approved area is known
    const features = selectedDrawing?.geoJSON?.features ?? [];
    const singleFeature =
      features.length === 1 && !isApprovedAreaLoading ? features[0] : undefined;
    const currentEventStatus = getValues('status');
    (getValues('warnings') ?? []).forEach((warning, warningIndex) => {
      if (
        isWarningFinalised({ status: warning.status ?? currentEventStatus })
      ) {
        return;
      }
      const areas = singleFeature
        ? [
            buildAreaFromFeature(
              singleFeature,
              warning.level as WarningLevel,
              approvedArea,
            ),
          ]
        : [];
      setValue(`warnings.${warningIndex}.areas`, areas, { shouldDirty: true });
      if (singleFeature) {
        void trigger(`warnings.${warningIndex}.areas`);
      }
    });
  }, [
    selectedDrawing,
    setValue,
    getValues,
    trigger,
    approvedArea,
    isApprovedAreaLoading,
  ]);

  const warningEventType = useWatch({ control, name: 'warningEventType' });
  const [selectedDays, setSelectedDays] = React.useState<DayRange>(() =>
    getDefaultDayRangeForType(getValues('warningEventType')),
  );

  const [compactIds, setCompactIds] = React.useState<Record<string, boolean>>(
    () =>
      isDefaultCollapsed
        ? Object.fromEntries(fields.map((field) => [field.id, true]))
        : {},
  );

  // Disable warning type field for warnings which have already been published. EARLY_WARNING should be changeable to WARNING still
  const isWarningTypeDisabled =
    selectedEvent?.warningEventType === WarningEventType.WARNING &&
    (eventStatus === PublicWarningStatus.DRAFT_PUBLISHED ||
      eventStatus === PublicWarningStatus.PUBLISHED);

  const revalidateProbabilities = React.useCallback((): void => {
    const currentWarnings = getValues('warnings') ?? [];
    currentWarnings.forEach((warning, warningIndex) => {
      // empty is only invalid on publish, don't flag it while editing
      if (isEmpty(warning.probability)) {
        return;
      }
      void trigger(`warnings.${warningIndex}.probability`);
    });
  }, [getValues, trigger]);

  const handleWarningTypeChange = (nextType: WarningEventType): void => {
    const isPublishedEvent =
      eventStatus === PublicWarningStatus.PUBLISHED ||
      eventStatus === PublicWarningStatus.DRAFT_PUBLISHED;
    const isUpgradeFromEarlyWarning =
      warningEventType === WarningEventType.EARLY_WARNING &&
      nextType === WarningEventType.WARNING;

    if (isPublishedEvent && isUpgradeFromEarlyWarning) {
      void confirmDialog({
        title: t('warning-event-type-change-confirm-title'),
        description: t('warning-event-type-change-confirm-description'),
        confirmLabel: t('warning-event-type-change-confirm-confirm'),
        cancelLabel: t('warning-dialog-cancel'),
        catchOnCancel: true,
      })
        .then(() => {
          setSelectedDays(getDefaultDayRangeForType(nextType));
        })
        .catch(() => {
          setValue('warningEventType', WarningEventType.EARLY_WARNING, {
            shouldDirty: true,
          });
        });
      return;
    }

    setSelectedDays(getDefaultDayRangeForType(nextType));
    revalidateProbabilities();
  };

  const texts = useWatch({ control, name: 'texts' });

  const updateSummaryTexts = (
    nextMode: WarningEventMode,
    currentTexts: WarningEventTexts,
  ): void => {
    (Object.keys(currentTexts) as (keyof WarningEventTexts)[]).forEach(
      (field) => {
        const isTranslationField = field.includes('Translation');
        const prefix = isTranslationField
          ? TEST_MESSAGE_PREFIX_TRANSLATION
          : TEST_MESSAGE_PREFIX_ORIGINAL;
        const value = currentTexts[field] ?? '';

        if (nextMode === WarningEventMode.TEST) {
          if (!value.startsWith(prefix)) {
            setValue(`texts.${field}`, `${prefix}${value}`, {
              shouldDirty: true,
            });
          }
        } else if (nextMode === WarningEventMode.ACTUAL) {
          if (value.startsWith(prefix)) {
            setValue(`texts.${field}`, value.slice(prefix.length), {
              shouldDirty: true,
            });
          }
        }
      },
    );
  };

  const handleWarningModeChange = (mode: WarningEventMode): void => {
    updateSummaryTexts(mode, texts);
  };

  const allCompact =
    fields.length > 0 && fields.every((field) => compactIds[field.id]);

  const toggleCompact = (id: string): void => {
    setCompactIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleToggleAllCompact = (): void => {
    setCompactIds(
      allCompact
        ? {}
        : Object.fromEntries(fields.map((field) => [field.id, true])),
    );
  };

  const handleDaysChange = (range: DayRange | null): void => {
    setSelectedDays(
      range ?? getDefaultDayRangeForType(getValues('warningEventType')),
    );
  };

  const timelineRange = React.useMemo(
    () => getTimelineRange(selectedDays),
    [selectedDays],
  );

  const [deleteCount, setDeleteCount] = React.useState(0);

  const onAddWarning = (): void => {
    const newWarning = buildDefaultWarningItem(getValues());
    const firstFinalisedIndex = (getValues('warnings') ?? []).findIndex(
      isWarningFinalised,
    );

    if (firstFinalisedIndex === -1) {
      append(newWarning);
      return;
    }
    insert(firstFinalisedIndex, newWarning);
  };

  const onDuplicateWarning = (index: number): void => {
    const currentWarnings = getValues('warnings') ?? [];
    const source = currentWarnings[index];
    if (!source) {
      return;
    }
    const duplicate = buildDuplicateWarningItem(source);
    const firstFinalisedIndex = currentWarnings.findIndex(isWarningFinalised);
    // insert right below the source, unless that would put it inside the
    // finalised block, then insert just before the first finalised warning
    const targetIndex =
      firstFinalisedIndex === -1
        ? Math.min(index + 1, currentWarnings.length)
        : Math.min(index + 1, firstFinalisedIndex);

    insert(targetIndex, duplicate);
  };

  const onDeleteWarning = (index: number): void => {
    remove(index);
    setDeleteCount((count) => count + 1);
  };

  // re-validate after the removal commits, react-hook-form shifts errors up
  React.useEffect(() => {
    if (!deleteCount) {
      return;
    }
    const remainingWarnings = getValues('warnings') ?? [];
    remainingWarnings.forEach((_warning, index) => {
      void trigger(`warnings.${index}.validUntil`);
    });
  }, [deleteCount, getValues, trigger]);

  useFormDirty({
    isDirty: Object.keys(dirtyFields).length > 0,
    isLoading: isPublishing,
    error: error?.message,
    onFormDirty,
    onSuccess: () => {
      // reset form dirty and update defaultvalues and formvalues after successfull saving
      reset(getValues());
    },
  });

  const handleSaveDraft = (): void => {
    if (!isPublishing) {
      toggleIsDraft(true);
      void handleSubmit(async (data) => {
        setIsPublishing(true);
        const valuesToPost = buildSubmitValues(data);
        await onSaveDraft(valuesToPost).catch(() => {});
        setIsPublishing(false);
      })();
    }
  };

  const handlePublish = (): void => {
    toggleIsDraft(false);
    void handleSubmit(() => {
      if (!isPublishing) {
        toggleIsDraft(true);
        void handleSubmit(async (data) => {
          setIsPublishing(true);
          const valuesToPost = buildSubmitValues(data);
          await onPublish(valuesToPost).catch(() => {});
          setIsPublishing(false);
        })();
      }
    })();
  };

  const onExpireWithdraw = (
    status: PublicWarningStatus,
    index: number,
  ): void => {
    const titleStatus =
      status === PublicWarningStatus.EXPIRED ? 'expire' : 'withdraw';
    void confirmDialog({
      title: t(`warning-event-${titleStatus}-confirm-title`),
      description: t(`warning-event-${titleStatus}-confirm-description`),
      confirmLabel: t(`warning-event-${titleStatus}-confirm-confirm`),
      cancelLabel: t('warning-dialog-cancel'),
    }).then(() => {
      resetField(`warnings.${index}`);
      setValue(`warnings.${index}.status`, status, {
        shouldDirty: true,
      });
      setIsUnpublishedExpiredWithdraw(true);
    });
  };

  const onEventExpireWithdraw = (
    status: PublicWarningStatus.EXPIRED | PublicWarningStatus.WITHDRAWN,
  ): void => {
    const titleStatus =
      status === PublicWarningStatus.EXPIRED ? 'expire' : 'withdraw';
    void confirmDialog({
      title: t(`warning-event-${titleStatus}-event-confirm-title`),
      description: t(`warning-event-${titleStatus}-event-confirm-description`),
      confirmLabel: t(`warning-dialog-${titleStatus}-confirm`),
      cancelLabel: t('warning-dialog-cancel'),
    }).then(() => {
      reset(getFormData(selectedEvent));
      getWarningStatusesForEventAction(getValues('warnings'), status).forEach(
        (newStatus, index) => {
          if (newStatus) {
            setValue(`warnings.${index}.status`, newStatus, {
              shouldDirty: true,
            });
          }
        },
      );
      setIsUnpublishedExpiredWithdraw(true);
      setPendingEventAction(status);
    });
  };

  const onChangeMode = (): void => {
    onChangeFormMode(!isReadOnly);
  };

  const { isRequired } = useDraftFormHelpers();

  const disabledWarningDeleteButton = fields.length <= 1;

  const isNewOrDraftEvent =
    selectedEvent?.status === undefined ||
    selectedEvent?.status === PublicWarningStatus.DRAFT;

  const publishButtonLabel = ((): string => {
    if (pendingEventAction === PublicWarningStatus.EXPIRED) {
      return t('warning-dialog-expire-confirm');
    }
    if (pendingEventAction === PublicWarningStatus.WITHDRAWN) {
      return t('warning-dialog-withdraw-confirm');
    }
    return t('public-warning-event-button-publish');
  })();

  return (
    <Box
      data-testid="warning-event-form-scroll-container"
      ref={ref}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'geowebColors.background.surfaceApp',
      }}
    >
      {!!errors && (
        <Box sx={{ marginBottom: 2 }}>
          <AlertBanner
            info={errors.errors as unknown as React.ReactElement}
            severity={error?.severity}
            shouldClose
            title={errors.title}
          />
        </Box>
      )}
      {!shouldHideFormToggleMode ? (
        <Box
          sx={{
            flexShrink: 0,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'flex-end',
            height: '30px',
            paddingRight: 0.5,
          }}
        >
          <Box sx={{ position: 'relative', top: -2, right: '16px' }}>
            <SwitchButton
              activeLabel={t('warning-editor-user-editor')}
              checked={!isReadOnly}
              inActiveLabel={t('warning-editor-user-viewer')}
              onChange={onChangeMode}
            />
          </Box>
          <Box
            sx={{
              position: 'relative',
              top: 8,
              right: '8px',
              width: '16px',
            }}
          >
            {!!currentEventEditor && (
              <WarningAvatar editor={currentEventEditor} />
            )}
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingX: 1,
          paddingY: 0.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'geowebColors.background.surface',
            borderRadius: 1,
            padding: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'top',
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ flex: '0.7 1 50px', position: 'relative' }}>
                <ReactHookFormSelect
                  disabled={areEventFieldsDisabled || !isNewOrDraftEvent}
                  label={t('public-warning-event-warning-mode')}
                  name="mode"
                  onChange={(event) =>
                    handleWarningModeChange(
                      event.target.value as WarningEventMode,
                    )
                  }
                >
                  {warningEventModes.map((mode) => (
                    <MenuItem key={mode.key} value={mode.key}>
                      {t(mode.translationKey)}
                    </MenuItem>
                  ))}
                </ReactHookFormSelect>
              </Box>
              <Box sx={{ flex: '1 1 110px', position: 'relative' }}>
                <ReactHookFormTextField
                  disabled={areEventFieldsDisabled}
                  label={t('public-warning-event-incident')}
                  name="incident"
                  onChange={async (): Promise<boolean> => trigger('incident')}
                  rules={{
                    validate: {
                      isOverMaxLength: (value): boolean | string =>
                        value.length > INCIDENT_MAX_LENGTH
                          ? t('warning-event-validation-incident-max-length', {
                              MAX_CHARS: INCIDENT_MAX_LENGTH,
                            })
                          : true,
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: '1.3 1 120px',
                  position: 'relative',
                }}
              >
                <Phenomenon
                  isDisabled={areEventFieldsDisabled || !isNewOrDraftEvent}
                  isReadOnly={isReadOnly}
                  isWarningEvent
                  onChange={() => {
                    void trigger('warningEventType');
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 145px', minWidth: '145px' }}>
                <ReactHookFormSelect
                  disabled={areEventFieldsDisabled || isWarningTypeDisabled}
                  label={t('public-warning-event-warning-type')}
                  name="warningEventType"
                  onChange={(event) =>
                    handleWarningTypeChange(
                      event.target.value as WarningEventType,
                    )
                  }
                  rules={{
                    validate: {
                      noEarlyWarningWithWaterspouts: (
                        value: string,
                      ): boolean | string =>
                        value === WarningEventType.EARLY_WARNING &&
                        getValues('phenomenon') === 'waterspouts'
                          ? t('warning-warning-level-error-waterspouts')
                          : true,
                    },
                  }}
                >
                  {warningEventTypes.map((type) => (
                    <MenuItem key={type.key} value={type.key}>
                      {t(type.translationKey)}
                    </MenuItem>
                  ))}
                </ReactHookFormSelect>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'top',
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <DaySelector
                  onChange={handleDaysChange}
                  selectedDays={selectedDays}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexShrink: 0,
                height: 56,
              }}
            >
              {showEventOptionsMenu ? (
                <ToggleMenu
                  buttonTestId="warning-event-options-button"
                  isDisabled={!hasPendingWarnings}
                  menuItems={[
                    {
                      text: t('warning-list-button-expire'),
                      icon: <Expire />,
                      isDisabled: !canExpire,
                      action: () =>
                        onEventExpireWithdraw(PublicWarningStatus.EXPIRED),
                    },
                    {
                      text: t('warning-list-button-withdraw'),
                      icon: <Remove />,
                      isDisabled: !canWithdraw,
                      action: () =>
                        onEventExpireWithdraw(PublicWarningStatus.WITHDRAWN),
                    },
                  ]}
                  menuTitle={t('area-manager-share-menu-options')}
                  tooltipTitle={t('public-warning-event-options')}
                />
              ) : null}
              <CollapseToggleButton
                collapseLabel={t('public-warning-event-collapse-all')}
                expandLabel={t('public-warning-event-expand-all')}
                isCollapsed={allCompact}
                onToggle={handleToggleAllCompact}
              />
            </Box>
          </Box>

          <Grid
            columnSpacing={2}
            container
            rowSpacing={2}
            sx={{ marginTop: 0.5 }}
          >
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ position: 'relative' }}>
                <ReactHookFormTextField
                  disabled={areEventFieldsDisabled}
                  label={t('public-warning-event-summary')}
                  multiline
                  name="texts.headlineOriginal"
                  rows={3}
                  rules={{
                    maxLength: SUMMARY_HEADLINE_LIMIT,
                    validate: {
                      isRequired,
                      minChars: (value: string): boolean | string =>
                        value.length && value.length < SUMMARY_FIELDS_MIN_LENGTH
                          ? t('warning-summary-error-min-chars')
                          : true,
                    },
                  }}
                />
                {!!showSummaryError && (
                  <FormHelperText error variant="filled">
                    {t(showSummaryError)}
                  </FormHelperText>
                )}
                <CustomIconButton
                  data-testid="warning-event-form-summary-button"
                  isSelected={isSummaryDialogOpen}
                  onClick={onOpenWarningSummary}
                  sx={{ position: 'absolute', right: 4, top: 73, zIndex: 1 }}
                  tooltipTitle={t('warning-event-open-summary-dialog-tooltip')}
                  variant="tool"
                >
                  <Edit />
                </CustomIconButton>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReactHookFormTextField
                disabled={areEventFieldsDisabled}
                label={t('public-warning-event-translation')}
                multiline
                name="texts.headlineTranslation"
                rows={3}
                rules={{
                  maxLength: SUMMARY_HEADLINE_LIMIT,
                  validate: {
                    isRequired,
                    minChars: (value: string): boolean | string =>
                      value.length && value.length < SUMMARY_FIELDS_MIN_LENGTH
                        ? t('warning-summary-error-min-chars')
                        : true,
                  },
                }}
              />
              {!!showTranslationError && (
                <FormHelperText error variant="filled">
                  {t(showTranslationError)}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </Box>
        <WarningEventTimelineRangeProvider range={timelineRange}>
          <ReactSortable
            animation={200}
            direction="vertical"
            handle=".handle"
            list={fields as { id: string }[]}
            onUpdate={({ oldIndex, newIndex }) => {
              if (
                oldIndex === undefined ||
                newIndex === undefined ||
                oldIndex === newIndex ||
                newIndex < 0 ||
                newIndex >= fields.length
              ) {
                return;
              }
              move(oldIndex, newIndex);
            }}
            setList={() => {}}
            tag="div"
          >
            {fields.map((field, index) => (
              <div key={field.id}>
                <WarningEventItem
                  approvedArea={approvedArea}
                  defaultMapPreset={defaultMapPreset}
                  hoveredDrawing={hoveredDrawing}
                  index={index}
                  isApprovedAreaLoading={isApprovedAreaLoading}
                  isCompact={!!compactIds[field.id]}
                  isDeleteButtonDisabled={disabledWarningDeleteButton}
                  isReadOnly={isReadOnly}
                  isSnapshot={isSnapshot}
                  onAddDrawing={onAddDrawing}
                  onDeleteWarning={onDeleteWarning}
                  onDuplicateWarning={onDuplicateWarning}
                  onExpireWithdraw={onExpireWithdraw}
                  onToggleCompact={() => toggleCompact(field.id)}
                  selectedDrawing={selectedDrawing}
                />
              </div>
            ))}
          </ReactSortable>
        </WarningEventTimelineRangeProvider>

        {!areEventFieldsDisabled && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={onAddWarning}
              startIcon={<Add />}
              sx={{ textTransform: 'none', fontSize: '12px' }}
              variant="tertiary"
            >
              {t('public-warning-event-add-warning')}
            </Button>
          </Box>
        )}
      </Box>
      <DraftFieldHelper />
      <ReactHookFormHiddenInput
        name="texts.conciseSummaryOriginal"
        rules={{
          maxLength: SUMMARY_CONCISE_SUMMARY_LIMIT,
          validate: {
            isRequired,
            minChars: (value: string): boolean | string =>
              value.length && value.length < SUMMARY_FIELDS_MIN_LENGTH
                ? t('warning-summary-error-min-chars')
                : true,
          },
        }}
      />
      <ReactHookFormHiddenInput
        name="texts.conciseSummaryTranslation"
        rules={{
          maxLength: SUMMARY_CONCISE_SUMMARY_LIMIT,
          validate: {
            isRequired,
            minChars: (value: string): boolean | string =>
              value.length && value.length < SUMMARY_FIELDS_MIN_LENGTH
                ? t('warning-summary-error-min-chars')
                : true,
          },
        }}
      />
      <ReactHookFormHiddenInput
        name="texts.descriptionOriginal"
        rules={{
          maxLength: SUMMARY_DESCRIPTION_LIMIT,
          validate: {
            isRequired: (value: string): boolean | string =>
              warningEventType === WarningEventType.EARLY_WARNING
                ? true
                : isRequired(value),
            minChars: (value: string): boolean | string =>
              warningEventType !== WarningEventType.EARLY_WARNING &&
              value.length &&
              value.length < SUMMARY_FIELDS_MIN_LENGTH
                ? t('warning-summary-error-min-chars')
                : true,
          },
        }}
      />
      <ReactHookFormHiddenInput
        name="texts.descriptionTranslation"
        rules={{
          maxLength: SUMMARY_DESCRIPTION_LIMIT,
          validate: {
            isRequired: (value: string): boolean | string =>
              warningEventType === WarningEventType.EARLY_WARNING
                ? true
                : isRequired(value),
            minChars: (value: string): boolean | string =>
              warningEventType !== WarningEventType.EARLY_WARNING &&
              value.length &&
              value.length < SUMMARY_FIELDS_MIN_LENGTH
                ? t('warning-summary-error-min-chars')
                : true,
          },
        }}
      />
      {!isReadOnly && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              '& button:not(:last-child)': { mr: 3 },
            }}
          >
            <Button
              disabled={isPublishing}
              onClick={(): void => {
                setPendingEventAction(null);
                onCancel();
              }}
              sx={{ textTransform: 'none', fontSize: '12px' }}
              variant="tertiary"
            >
              {t('public-warning-event-button-cancel')}
            </Button>
            {
              // Only show delete button if event has been stored already and is in DRAFT status
              selectedEvent?.eventId &&
              (selectedEvent?.status === PublicWarningStatus.DRAFT ||
                selectedEvent?.status ===
                  PublicWarningStatus.DRAFT_PUBLISHED) ? (
                <Button
                  color="error"
                  disabled={isPublishing}
                  onClick={onDelete}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                  variant="tertiary"
                >
                  {t('public-warning-event-button-delete')}
                </Button>
              ) : null
            }
            <Tooltip
              title={
                isUnpublishedExpiredWithdraw
                  ? t('warning-event-save-draft-disabled-withdraw-expire')
                  : ''
              }
            >
              <Box sx={{ marginRight: '24px' }}>
                <Button
                  disabled={isPublishing || isUnpublishedExpiredWithdraw}
                  onClick={handleSaveDraft}
                  sx={{ textTransform: 'none', fontSize: '12px' }}
                  variant="tertiary"
                >
                  {t('public-warning-event-button-save-draft')}
                </Button>
              </Box>
            </Tooltip>
            <Button
              disabled={isPublishing}
              onClick={handlePublish}
              sx={{ textTransform: 'none', fontSize: '12px' }}
              variant="primary"
            >
              {publishButtonLabel}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
